import { apiRequest } from '../client';
import type { PageResponse } from '../types';

/** Mirrors backend in.voltpassport.ordermgmt.api.SalesOrderStatus */
export type SalesOrderStatus =
  | 'DRAFT'
  | 'CONFIRMED'
  | 'IN_PRODUCTION'
  | 'SHIPPED'
  | 'DELIVERED'
  | 'CANCELLED';

export interface SalesOrderView {
  id: string;
  code: string;
  /** Nullable for legacy rows. */
  customerId: string | null;
  customerCode: string;
  customerName: string;
  /** Optional link back to the quotation this order was raised from. */
  quotationId: string | null;
  totalAmount: string;
  currency: string;
  status: SalesOrderStatus;
  createdAt: string;
}

export interface SalesOrderSummary {
  id: string;
  code: string;
  customerCode: string;
  totalAmount: string;
  currency: string;
  status: SalesOrderStatus;
}

/**
 * Post-Wave-1 shape: `code` is server-generated. Customer identity is a
 * mandatory FK; the quotation link stays optional.
 */
export interface CreateSalesOrderRequest {
  customerId: string;
  quotationId?: string;
  totalAmount: string;
  currency: string;
}

export interface TransitionSalesOrderRequest {
  next: SalesOrderStatus;
}

/**
 * State machine — what statuses a sales order can transition to from its
 * current status. Mirrors the backend's SalesOrder aggregate.
 *
 * DRAFT → CONFIRMED → IN_PRODUCTION → SHIPPED → DELIVERED
 * CANCELLED reachable from any pre-shipped state; SHIPPED cannot cancel.
 */
export const SALES_ORDER_TRANSITIONS: Record<SalesOrderStatus, SalesOrderStatus[]> = {
  DRAFT: ['CONFIRMED', 'CANCELLED'],
  CONFIRMED: ['IN_PRODUCTION', 'CANCELLED'],
  IN_PRODUCTION: ['SHIPPED', 'CANCELLED'],
  SHIPPED: ['DELIVERED'],
  DELIVERED: [],
  CANCELLED: []
};

export const salesOrdersApi = {
  list: (page = 0, size = 20) =>
    apiRequest<PageResponse<SalesOrderView>>(
      `/api/sales-orders?page=${page}&size=${size}`
    ),

  summaries: (page = 0, size = 20) =>
    apiRequest<PageResponse<SalesOrderSummary>>(
      `/api/sales-orders/summaries?page=${page}&size=${size}`
    ),

  get: (id: string) => apiRequest<SalesOrderView>(`/api/sales-orders/${id}`),

  create: (request: CreateSalesOrderRequest) =>
    apiRequest<SalesOrderView>('/api/sales-orders', {
      method: 'POST',
      body: request
    }),

  transition: (id: string, next: SalesOrderStatus) =>
    apiRequest<SalesOrderView>(`/api/sales-orders/${id}/transitions`, {
      method: 'POST',
      body: { next } satisfies TransitionSalesOrderRequest
    })
};
